import { Box, Button, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import { Formik } from 'formik'
import type { ReactElement } from 'react'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { PermissionsEnum } from '../../../../utils'
import type { CmFieldResponseObject } from '../../../interfaces'
import type {
  CmFormValues,
  FileDetailsResponse,
} from '../../../interfaces/FileDetailsResponse'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import PermissionGuard from '../../PermissionGuard'
import CMBooleanField from './CustomMetadataForm/CMBooleanField'
import CMDateField from './CustomMetadataForm/CMDateField'
import CMMultiSelectField from './CustomMetadataForm/CMMultiSelectField'
import CMNumberField from './CustomMetadataForm/CMNumberField'
import CMSingleSelectField from './CustomMetadataForm/CMSingleSelectField'
import CMTextareaField from './CustomMetadataForm/CMTextareaField'
import CMTextField from './CustomMetadataForm/CMTextField'

const ComponentMap: { [key: string]: (cmField: CMFieldProps) => ReactElement } =
  {
    Text: CMTextField,
    Textarea: CMTextareaField,
    Number: CMNumberField,
    Date: CMDateField,
    Boolean: CMBooleanField,
    SingleSelect: CMSingleSelectField,
    MultiSelect: CMMultiSelectField,
  }

const CustomMetadata = ({
  fileDetails: { customMetadata: data },
}: {
  fileDetails: FileDetailsResponse
}): ReactElement => {
  const { t } = useTranslation()

  const { cmFields } = useContext(CustomMetadataContext)

  if (cmFields.length) {
    return (
      <Formik<CmFormValues> initialValues={data} onSubmit={() => {}}>
        {() => (
          <VStack
            width="full"
            spacing="2"
            flex-grow="1"
            alignItems="flex-start"
            overflowY="auto"
            py="4"
            justifyContent="flex-start"
            height="full"
          >
            {cmFields.map(cmField => {
              const Component = ComponentMap[cmField.schema.type]
              return (
                <Component key={cmField.id} cmField={cmField} disabled={true} />
              )
            })}
          </VStack>
        )}
      </Formik>
    )
  }
  return (
    <VStack
      justifyContent="space-between"
      spacing="0"
      width="100%"
      height="100%"
    >
      <PermissionGuard
        permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
      >
        {
          (hasImageSettingsManagePermission: boolean) => (
            hasImageSettingsManagePermission ? (
              <Box width="full" backgroundColor="appWhite" flexShrink="0">
                <Button
                  as={Link}
                  to="/settings/media-library"
                  variant="solid"
                  isFullWidth
                >
                  +{t('add_new', 'Add New')}
                </Button>

                <Divider color="appGrey" width="auto" mt="1em" ml="-1em" mr="-1em" />
              </Box>
            ) : <></>
          )
        }
      </PermissionGuard>
    </VStack>
  )
}

export default CustomMetadata

export interface CMFieldProps {
  cmField: CmFieldResponseObject
  disabled?: boolean
}
